import { createSlice } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { FbPageStatus } from 'apps/addNewAccount/FacebookMessenger/addFbAccountInterfaces'
import { AddFbAccountState } from 'apps/addNewAccount/FacebookMessenger/redux/interfaces'
import {
  alertConnectionBlockedActiveTrial,
  alertConnectionRestricted,
} from 'apps/addNewAccount/utils/alerts'
import { ERROR_NOTIFICATIONS } from 'apps/addNewAccount/utils/constants'
import { alert } from 'common/core'
import { ChannelType } from 'common/core/constants/ChannelType'
import { AuthApi } from 'shared/api/requests/auth'
import { createAsyncAction } from 'shared/lib/redux'

const initialState: AddFbAccountState = {
  pages: [],
}

export const connectFbPage = createAsyncAction(
  'addFbAccount/connectFbPage',
  async ({ pageId, accountId }: { pageId: string; accountId: string | null }) => {
    const { data } = await AuthApi.addFbAccount({
      body: { page_id: pageId, account_id: accountId },
    })

    return data
  },
  {
    onBusinessError: (error) => {
      if (AuthApi.addFbAccount.isConnectionRestrictedError(error)) {
        alertConnectionRestricted(error.message)
        error.handle()
        return
      }

      if (AuthApi.addIgAccount.isComplianceError(error)) {
        alert(l.getString(ERROR_NOTIFICATIONS.CONNECTION_RESTRICTED_COMPLIANCE), 'danger', {
          isTemporary: false,
        })
        error.handle()
        return
      }

      if (AuthApi.addIgAccount.isLimitExceededError(error)) {
        alert(l.getString(ERROR_NOTIFICATIONS.CONNECTION_LIMIT_EXCEEDED), 'danger', {
          isTemporary: false,
        })
        error.handle()
        return
      }

      if (AuthApi.addIgAccount.isBlockedActiveTrialError(error)) {
        alertConnectionBlockedActiveTrial(ChannelType.FB)
        error.handle()
      }
    },
  },
)

export const requestPageAccess = createAsyncAction(
  'addFbAccount/requestPageAccess',
  async (pageId: string) => {
    await AuthApi.requestAccountAccess({ body: { fb_page_id: pageId } })

    return pageId
  },
)

const addFbAccountSlice = createSlice({
  name: 'addFbAccount/fetch',
  initialState,
  reducers: {
    setPages(state, action) {
      state.pages = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(connectFbPage.pending, (state, action) => {
      const payloadBody = action.meta.arg

      state.processingPageId = payloadBody.pageId
    })

    builder.addCase(connectFbPage.fulfilled, (state, action) => {
      if (!action.payload) {
        delete state.processingPageId
        return
      }

      state.accountId = action.payload.account_id

      delete state.processingPageId
    })

    builder.addCase(connectFbPage.rejected, (state) => {
      delete state.processingPageId
    })

    builder.addCase(requestPageAccess.pending, (state, action) => {
      const pageId = action.meta.arg

      state.processingPageId = pageId
    })

    builder.addCase(requestPageAccess.fulfilled, (state) => {
      const page = state.pages?.find((page) => page.id === state.processingPageId)

      if (page !== undefined) {
        page.status = FbPageStatus.REQUEST_ACCESS_PENDING
      }

      delete state.processingPageId
    })
    builder.addCase(requestPageAccess.rejected, (state) => {
      delete state.processingPageId
    })
  },
})

export const { reducer, actions } = addFbAccountSlice

export const addFbAccountActions = {
  ...actions,
}
